// src/lib/filters/validator.ts
//
// Filter validation for staged filter blocks.
//
// Returns a structured result that callers can use to emit toast + GlitchTip
// telemetry. This module is pure: it never logs, throws, or invokes side
// effects. Side effects (captureError, toast) belong in the caller.
//
// Mirrors the inline drop logic that previously lived in
// `ContactFilters.tsx::applyFilters`, including the silent paths near the end
// of that block (between-without-end, invalid date strings, missing table/
// field/operator). Each dropped condition produces a `DroppedCondition` so
// the UI can show a non-success toast and so GlitchTip sees the failure.
//
// `FilterBlock` mirrors the shape used in `ContactFilters.tsx` (which is a
// superset of `rpc.ts::FilterCondition` — it additionally tracks `relative*`
// client-only state). We re-declare it here rather than import to keep this
// module self-contained and easy to test.

export type DroppedReason =
  | 'missing-value'
  | 'missing-value-end'
  | 'invalid-date'
  | 'missing-relative-value'
  | 'missing-relative-value-end'
  | 'missing-table-field-operator';

export interface DroppedCondition {
  table?: string;
  field: string;
  operator: string;
  reason: DroppedReason;
  /** Friendly label for toast copy, e.g. "Important date (missing end date for 'between')". */
  humanReason: string;
}

// Field key (`table.field` or `custom_field:UUID`) → display label.
// Mirrors STANDARD_FILTER_FIELDS in `ContactFilters.tsx`. Custom fields fall
// through to the raw field key (acceptable for v1 per the spec).
const FIELD_LABELS: Record<string, string> = {
  'contacts.first_name': 'First Name',
  'contacts.last_name': 'Last Name',
  'contacts.type': 'Type',
  'contacts.rank': 'Rank',
  'contacts.status': 'Status',
  'contacts.relationship': 'Relationship',
  'contacts.company_name': 'Company',
  'contacts.job_title': 'Job Title',
  'contacts.industry': 'Industry',
  'contacts.source': 'Source',
  'contacts.import_batch': 'Import Batch',
  'contacts.created_at': 'Created Date',
  'contacts.imported_at': 'Imported At',
  'contacts.next_contact': 'Next Contact',
  'contacts.last_contacted': 'Last Contacted',
  'email_address.email': 'Email',
  'email_address.type': 'Email Type',
  'email_address.created_at': 'Email Created At',
  'physical_addresses.city': 'City',
  'physical_addresses.state': 'State',
  'physical_addresses.postal_code': 'Zip / Postal code',
  'physical_addresses.country': 'Country',
  'contact_interests.interest': 'Interest',
  'contact_interests.created_at': 'Interest Created At',
  'groups.group_name': 'Group Name',
  'important_dates.label': 'Important Date Label',
  'important_dates.date': 'Important Date',
  'activities.call_outcome': 'Call Outcome',
};

const REASON_SUFFIX: Record<DroppedReason, string> = {
  'missing-value': 'missing value',
  'missing-value-end': "missing end date for 'between'",
  'invalid-date': 'invalid date',
  'missing-relative-value': 'missing relative value',
  'missing-relative-value-end': "missing end value for 'between'",
  'missing-table-field-operator': 'incomplete filter',
};

function fieldLabel(table: string | undefined, field: string): string {
  if (table === 'custom_field' || field.startsWith('custom_field:')) {
    // Custom fields use `custom_field:<uuid>` for `field`. v1: raw value.
    return field;
  }
  const key = table ? `${table}.${field}` : field;
  return FIELD_LABELS[key] ?? key;
}

function buildHumanReason(
  table: string | undefined,
  field: string,
  reason: DroppedReason,
): string {
  return `${fieldLabel(table, field)} (${REASON_SUFFIX[reason]})`;
}

// ---------------------------------------------------------------------------
// Local types (subset of UI's FilterCondition / FilterBlock — enough for
// validation logic). Kept loose with optional fields so callers don't have to
// import every UI-only key.
// ---------------------------------------------------------------------------

export interface ValidatableCondition {
  id: string;
  table?: string;
  field: string;
  operator: string;
  value?: string;
  valueEnd?: string;
  label?: string;
  dateMode?: 'absolute' | 'relative';
  relativeValue?: number;
  relativeUnit?: 'days';
  relativeDirection?: 'ago' | 'from_now';
  relativeValueEnd?: number;
  relativeDirectionEnd?: 'ago' | 'from_now';
  matchYear?: boolean;
}

export interface FilterBlock {
  id: string;
  type: 'AND' | 'OR';
  conditions: ValidatableCondition[];
}

export interface ValidationResult {
  /** Only blocks that retained >=1 valid condition. */
  blocks: FilterBlock[];
  /** Every condition that was dropped (across all blocks), with reason. */
  dropped: DroppedCondition[];
  /** True iff nothing was dropped. */
  allValid: boolean;
  /** True iff at least one block (with >=1 condition) survived. */
  anyApplied: boolean;
}

// ---------------------------------------------------------------------------
// Single-condition validation. Returns null if the condition is valid, or a
// DroppedCondition describing why it was rejected.
// ---------------------------------------------------------------------------

function validateCondition(c: ValidatableCondition): DroppedCondition | null {
  // is_empty / is_not_empty: only require table + field + operator.
  if (c.operator === 'is_empty' || c.operator === 'is_not_empty') {
    if (!c.table || !c.field || !c.operator) {
      return {
        table: c.table,
        field: c.field,
        operator: c.operator,
        reason: 'missing-table-field-operator',
        humanReason: buildHumanReason(c.table, c.field, 'missing-table-field-operator'),
      };
    }
    return null;
  }

  // Relative date mode: requires computed `value` (and `valueEnd` for between).
  // NOTE: callers should have already run `computeAbsoluteDateFromRelative`
  // to populate `value`/`valueEnd`. A missing computed value means either the
  // relative number wasn't entered or the relative compute returned empty.
  if (c.dateMode === 'relative') {
    if (!c.value) {
      return {
        table: c.table,
        field: c.field,
        operator: c.operator,
        reason: 'missing-relative-value',
        humanReason: buildHumanReason(c.table, c.field, 'missing-relative-value'),
      };
    }
    if (c.operator === 'between' && !c.valueEnd) {
      return {
        table: c.table,
        field: c.field,
        operator: c.operator,
        reason: 'missing-relative-value-end',
        humanReason: buildHumanReason(c.table, c.field, 'missing-relative-value-end'),
      };
    }
    if (!c.table || !c.field || !c.operator) {
      return {
        table: c.table,
        field: c.field,
        operator: c.operator,
        reason: 'missing-table-field-operator',
        humanReason: buildHumanReason(c.table, c.field, 'missing-table-field-operator'),
      };
    }
    return null;
  }

  // Absolute date mode: must have value.
  if (c.dateMode === 'absolute') {
    if (!c.value) {
      return {
        table: c.table,
        field: c.field,
        operator: c.operator,
        reason: 'missing-value',
        humanReason: buildHumanReason(c.table, c.field, 'missing-value'),
      };
    }
    if (c.operator === 'between' && !c.valueEnd) {
      return {
        table: c.table,
        field: c.field,
        operator: c.operator,
        reason: 'missing-value-end',
        humanReason: buildHumanReason(c.table, c.field, 'missing-value-end'),
      };
    }
    if (!c.table || !c.field || !c.operator) {
      return {
        table: c.table,
        field: c.field,
        operator: c.operator,
        reason: 'missing-table-field-operator',
        humanReason: buildHumanReason(c.table, c.field, 'missing-table-field-operator'),
      };
    }
    return null;
  }

  // Non-date-mode 'between': both ends required.
  if (c.operator === 'between') {
    if (!c.value) {
      return {
        table: c.table,
        field: c.field,
        operator: c.operator,
        reason: 'missing-value',
        humanReason: buildHumanReason(c.table, c.field, 'missing-value'),
      };
    }
    if (!c.valueEnd) {
      return {
        table: c.table,
        field: c.field,
        operator: c.operator,
        reason: 'missing-value-end',
        humanReason: buildHumanReason(c.table, c.field, 'missing-value-end'),
      };
    }
  }

  // before/after/between with a parseable date.
  if (['before', 'after', 'between'].includes(c.operator)) {
    if (!c.value) {
      return {
        table: c.table,
        field: c.field,
        operator: c.operator,
        reason: 'missing-value',
        humanReason: buildHumanReason(c.table, c.field, 'missing-value'),
      };
    }
    if (new Date(c.value).toString() === 'Invalid Date') {
      return {
        table: c.table,
        field: c.field,
        operator: c.operator,
        reason: 'invalid-date',
        humanReason: buildHumanReason(c.table, c.field, 'invalid-date'),
      };
    }
    if (c.operator === 'between' && c.valueEnd && new Date(c.valueEnd).toString() === 'Invalid Date') {
      return {
        table: c.table,
        field: c.field,
        operator: c.operator,
        reason: 'invalid-date',
        humanReason: buildHumanReason(c.table, c.field, 'invalid-date'),
      };
    }
  }

  // Fallback: require table + field + operator + non-empty string value.
  if (!c.table || !c.field || !c.operator) {
    return {
      table: c.table,
      field: c.field,
      operator: c.operator,
      reason: 'missing-table-field-operator',
      humanReason: buildHumanReason(c.table, c.field, 'missing-table-field-operator'),
    };
  }
  if (typeof c.value !== 'string' || !c.value.trim()) {
    return {
      table: c.table,
      field: c.field,
      operator: c.operator,
      reason: 'missing-value',
      humanReason: buildHumanReason(c.table, c.field, 'missing-value'),
    };
  }

  return null;
}

/**
 * Validate a list of filter blocks. Drops invalid conditions and blocks with
 * zero remaining conditions. Returns the filtered blocks plus a per-drop
 * report for telemetry + user-facing messaging.
 *
 * The caller is responsible for:
 *   - resolving relative dates to absolute `value`/`valueEnd` BEFORE calling
 *     this function (so the validator sees the final shape);
 *   - emitting `captureError` for each `DroppedCondition`;
 *   - displaying the appropriate toast based on `allValid` / `anyApplied`.
 */
export function validateFilterBlocks(blocks: FilterBlock[]): ValidationResult {
  const dropped: DroppedCondition[] = [];

  const filteredBlocks: FilterBlock[] = blocks
    .map((block) => {
      const validConditions: ValidatableCondition[] = [];
      for (const c of block.conditions) {
        const drop = validateCondition(c);
        if (drop) {
          dropped.push(drop);
        } else {
          validConditions.push(c);
        }
      }
      return { ...block, conditions: validConditions };
    })
    .filter((block) => block.conditions.length > 0);

  return {
    blocks: filteredBlocks,
    dropped,
    allValid: dropped.length === 0,
    anyApplied: filteredBlocks.length > 0,
  };
}
